(() => {
  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[char]);

  const setupGallery = (root) => {
    const mainImage = root.querySelector('[data-ga-gallery-main], .ga-gallery__main-image');
    const mainFrame = root.querySelector('[data-ga-gallery-main-frame]');
    const mainLink = root.querySelector('[data-ga-gallery-link]');
    const previous = root.querySelector('[data-ga-gallery-prev]');
    const next = root.querySelector('[data-ga-gallery-next]');
    const thumbs = [...root.querySelectorAll('[data-ga-gallery-thumb]')];
    if (!mainImage || !mainFrame) return null;

    const galleryImageUrl = (thumb) => (thumb?.dataset.gaImage || '').replace(/([?&])width=\d+/, '$1width=1200');
    thumbs.forEach((thumb) => {
      const source = galleryImageUrl(thumb);
      if (!source) return;
      const preload = new Image();
      preload.decoding = 'async';
      preload.src = source;
    });

    const selectThumb = (thumb) => {
      if (!thumb) return;
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.src = galleryImageUrl(thumb);
      mainImage.alt = thumb.dataset.gaAlt || '';
      if (mainLink) mainLink.href = thumb.dataset.gaFullImage || thumb.dataset.gaImage;
      mainFrame.dataset.mediaId = thumb.dataset.mediaId || '';
      thumbs.forEach((item) => {
        const active = item === thumb;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      thumb.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
    };

    const move = (direction) => {
      if (!thumbs.length) return;
      const activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
      selectThumb(thumbs[(activeIndex + direction + thumbs.length) % thumbs.length]);
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => selectThumb(thumb)));
    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    return { selectByMediaId: (id) => selectThumb(thumbs.find((thumb) => thumb.dataset.mediaId === String(id))) };
  };

  const setupRecentlyViewed = (root) => {
    const dataNode = root.querySelector('[data-ga-current-product]');
    const section = document.querySelector('[data-ga-recent]');
    const grid = section?.querySelector('[data-ga-recent-grid]');
    if (!dataNode || !section || !grid) return;

    try {
      const current = JSON.parse(dataNode.textContent);
      const key = 'ga-recently-viewed-products';
      const stored = JSON.parse(localStorage.getItem(key) || '[]');
      const recent = stored.filter((item) => item?.handle && item.handle !== current.handle).slice(0, 4);

      if (recent.length) {
        grid.innerHTML = recent.map((item) => '<article class="ga-card"><a href="' + escapeHtml(item.url) + '">' + (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy" width="720" height="720">' : '') + '<div class="ga-card__body"><h3>' + escapeHtml(item.title) + '</h3><p>' + escapeHtml(item.price) + '</p></div></a></article>').join('');
        section.hidden = false;
      }

      const next = [current, ...stored.filter((item) => item?.handle && item.handle !== current.handle)].slice(0, 8);
      localStorage.setItem(key, JSON.stringify(next));
    } catch (_) {
      section.hidden = true;
    }
  };

  const shuffleRelatedProducts = (viewport) => {
    if (!viewport?.hasAttribute('data-ga-related-shuffle')) return;
    const cards = [...viewport.querySelectorAll('.ga-card')];
    for (let index = cards.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [cards[index], cards[randomIndex]] = [cards[randomIndex], cards[index]];
    }
    const fragment = document.createDocumentFragment();
    cards.forEach((card) => fragment.appendChild(card));
    viewport.appendChild(fragment);
  };

  const setupCarousels = () => {
    document.querySelectorAll('[data-ga-carousel]').forEach((section) => {
      if (section.dataset.gaCarouselReady === 'true') return;
      const viewport = section.querySelector('[data-ga-carousel-viewport]');
      const previous = section.querySelector('[data-ga-carousel-prev]');
      const next = section.querySelector('[data-ga-carousel-next]');
      shuffleRelatedProducts(viewport);
      const cards = viewport ? [...viewport.querySelectorAll('.ga-card')] : [];
      if (!viewport || !previous || !next) return;
      section.dataset.gaCarouselReady = 'true';
      if (cards.length < 2) {
        previous.hidden = true;
        next.hidden = true;
        return;
      }
      const step = () => {
        const card = viewport.querySelector('.ga-card');
        const gap = parseFloat(getComputedStyle(viewport).gap || '0');
        return card ? card.getBoundingClientRect().width + gap : viewport.clientWidth;
      };
      const move = (direction) => {
        const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        if (maximum <= 2) return;
        if (direction > 0 && viewport.scrollLeft >= maximum - 3) {
          viewport.scrollTo({ left: 0, behavior: 'smooth' });
        } else if (direction < 0 && viewport.scrollLeft <= 3) {
          viewport.scrollTo({ left: maximum, behavior: 'smooth' });
        } else {
          viewport.scrollBy({ left: direction * step(), behavior: 'smooth' });
        }
      };
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
      });
    });
  };

  const setupCounters = (root) => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    root.querySelectorAll('[data-ga-counter]').forEach((counter) => {
      if (counter.dataset.gaCounterReady === 'true') return;
      counter.dataset.gaCounterReady = 'true';
      const target = Math.max(0, Number.parseInt(counter.dataset.target || '0', 10) || 0);
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en');
      if (prefersReducedMotion || target <= 1) {
        counter.textContent = '+' + formatter.format(target);
        return;
      }
      const distance = Math.min(target, 40 + Math.floor(Math.random() * 81));
      let current = Math.max(0, target - distance);
      counter.textContent = '+' + formatter.format(current);
      const tick = () => {
        if (current >= target) return;
        const remaining = target - current;
        const step = Math.min(remaining, 1 + Math.floor(Math.random() * Math.min(10, remaining)));
        current += step;
        counter.textContent = '+' + formatter.format(current);
        window.setTimeout(tick, 75 + Math.floor(Math.random() * 126));
      };
      window.setTimeout(tick, 120 + Math.floor(Math.random() * 181));
    });
  };

  const init = (root) => {
    if (root.dataset.gaReady === 'true') return;
    root.dataset.gaReady = 'true';
    const gallery = setupGallery(root);
    setupRecentlyViewed(root);
    setupCarousels();
    setupCounters(root);
    const form = root.querySelector('[data-ga-product-form]');
    const variantInput = root.querySelector('[data-ga-variant-id]');
    const variantData = root.querySelector('[data-ga-variants]');
    if (!form || !variantInput || !variantData) return;
    let variants = [];
    try { variants = JSON.parse(variantData.textContent); } catch (_) { return; }
    const selects = [...root.querySelectorAll('[data-ga-option]')];
    const colorInputs = [...root.querySelectorAll('[data-ga-color-value]')];
    const update = () => {
      const values = selects.map((select) => select.value);
      const variant = variants.find((item) => item.options.every((value, index) => value === values[index]));
      if (!variant) return;
      variantInput.value = variant.id;
      const price = root.querySelector('[data-ga-price]');
      const stock = root.querySelector('[data-ga-stock]');
      const submit = root.querySelector('[data-ga-submit]');
      if (price) price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(variant.price / 100);
      if (stock) { stock.textContent = variant.available ? 'In stock' : 'Sold out'; stock.dataset.stockState = variant.available ? 'available' : 'sold-out'; }
      if (submit) { submit.disabled = !variant.available; submit.textContent = variant.available ? 'Add to cart' : 'Sold out'; }
      if (variant.featured_media) gallery?.selectByMediaId(variant.featured_media.id);
    };
    selects.forEach((select) => select.addEventListener('change', update));
    colorInputs.forEach((input) => input.addEventListener('change', () => {
      if (!input.checked) return;
      const select = root.querySelector('[data-ga-option][data-option-position=\"' + input.dataset.optionPosition + '\"]');
      if (!select) return;
      select.value = input.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    update();
  };
  const initAll = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-ga-product-root]')) roots.push(scope);
    roots.push(...scope.querySelectorAll('[data-ga-product-root]'));
    roots.forEach(init);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();